import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../auth/auth/hooks';
import Connect4Provider, { useConnect4Socket } from './connect4-provider';
import QueueScreen from './queue-screen';
import GameBoard from './game-board';

// States: connecting | queue | playing
function Connect4Inner() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const user = useAuthUser();
    const socket = useConnect4Socket();

    const [phase, setPhase] = useState('connecting'); // connecting | queue | playing
    const [gameState, setGameState] = useState(null);

    const playerName = user?.name || user?.nickname || 'Player';

    // Move to queue once connected
    useEffect(() => {
        if (socket && phase === 'connecting') {
            setPhase('queue');
        }
    }, [socket, phase]);

    // Listen for game start
    useEffect(() => {
        if (!socket) return;

        const handleStart = (data) => {
            setGameState(data);
            setPhase('playing');
        };

        socket.on('game:start', handleStart);
        return () => socket.off('game:start', handleStart);
    }, [socket]);

    const goBack = useCallback(() => {
        navigate('/main/applications');
    }, [navigate]);

    const handleCancel = useCallback(() => {
        if (socket) socket.emit('queue:leave');
        setPhase('connecting');
        goBack();
    }, [socket, goBack]);

    const handleGameBack = useCallback(() => {
        setGameState(null);
        setPhase(socket ? 'queue' : 'connecting');
    }, [socket]);

    if (phase === 'connecting') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <CircularProgress size={48} />
                <Typography variant="h6" color="text.secondary">
                    {t('APPLICATIONS.CONNECT4.CONNECTING')}
                </Typography>
            </Box>
        );
    }

    if (phase === 'queue') {
        return (
            <QueueScreen
                playerName={playerName}
                onCancel={handleCancel}
                onBack={goBack}
            />
        );
    }

    if (phase === 'playing' && gameState) {
        return (
            <GameBoard
                gameState={gameState}
                onBack={handleGameBack}
            />
        );
    }

    return null;
}

export default function Connect4() {
    return (
        <Connect4Provider>
            <Connect4Inner />
        </Connect4Provider>
    );
}
